/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DATE_FORMAT, regleEtatMatrimonial } from '@/constants';

import validationInformationsNaissance from '../validationInformationsNaissance';

dayjs.extend(customParseFormat);

/**
 * Validation de l'étape « Défunt ».
 *
 * L'état civil réutilise `validationInformationsNaissance` en mode « individu
 * principal » : nom, prénom usuel, sexe, date **et** lieu de naissance
 * obligatoires — ce dernier figure sur l'acte papier et est renseigné en base.
 *
 * S'y ajoutent deux règles propres au décès :
 *
 * - la date de naissance doit précéder la date de décès (le contrôle
 *   « avant la date de l'acte » de la validation d'état civil ne suffit pas :
 *   une transcription peut être dressée des années après) ;
 * - la date de situation matrimoniale, quand elle est saisie, doit être valide
 *   et antérieure ou égale au décès.
 *
 * ⚠️ L'état matrimonial lui-même reste OPTIONNEL : quelques actes n'en portent
 * aucun, l'exiger empêcherait de les numériser.
 */
const validationStepDefunt = (data: Record<string, any>) => {
  const errors: Record<string, string> = {
    ...validationInformationsNaissance(data),
  };

  const { anneeNaissance, moisNaissance, jourNaissance } = data ?? {};
  const dateNaissance = dayjs(
    `${anneeNaissance}-${moisNaissance}-${jourNaissance}`,
    'YYYY-M-D',
  );
  const dateDeces = dayjs(data?.dateDeces, DATE_FORMAT, true);

  if (
    _.isNil(errors.dateNaissance) &&
    dateNaissance.isValid() &&
    dateDeces.isValid() &&
    dateNaissance.isAfter(dateDeces, 'day')
  ) {
    errors.dateNaissance =
      'La date de naissance doit être antérieure à la date de décès';
  }

  /*
   * La date n'est contrôlée que si la situation en admet une : sur les autres
   * elle n'est même pas affichée, et une valeur résiduelle d'une situation
   * précédente ne doit pas bloquer l'étape — le back la purge.
   */
  const admetUneDate = !_.isNil(
    regleEtatMatrimonial(data?.etatMatrimonialCode).libelleDate,
  );

  if (admetUneDate && !_.isEmpty(data?.dateEtatMatrimonial)) {
    const dateEtatMatrimonial = dayjs(
      data.dateEtatMatrimonial,
      DATE_FORMAT,
      true,
    );

    if (!dateEtatMatrimonial.isValid()) {
      errors.dateEtatMatrimonial = 'La date est invalide';
    } else if (
      dateDeces.isValid() &&
      dateEtatMatrimonial.isAfter(dateDeces, 'day')
    ) {
      errors.dateEtatMatrimonial =
        'La date de situation matrimoniale doit être antérieure au décès';
    }
  }

  return _.pickBy(errors, (value) => !_.isEmpty(value));
};

export default validationStepDefunt;
